<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useAuthClient } from '@/stores/auth-store'
const auth = useAuthClient()
const { sessionStatus, reconnectOpen, account, hasLocalAccount } = auth
const email = ref(account.value?.email || '')
const code = ref('')
const codeSent = ref(false)
const busy = ref(false)
const error = ref('')
const info = ref('')
const dialogPanel = ref<HTMLElement | null>(null)
let previousFocus: HTMLElement | null = null
watch(reconnectOpen, async (open) => {
  if (open) {
    email.value = account.value?.email || email.value
    error.value = ''
    info.value = ''
    previousFocus = document.activeElement as HTMLElement | null
    await nextTick()
    dialogPanel.value?.querySelector<HTMLInputElement>('input')?.focus()
  } else previousFocus?.focus()
})
watch(email, () => {
  codeSent.value = false
  code.value = ''
})
async function submit() {
  busy.value = true
  error.value = ''
  info.value = ''
  try {
    const verifying = codeSent.value
    const res = await auth.post(
      `/rpc/users/auth/${verifying ? 'verify-code' : 'reconnect'}`,
      {
        email: email.value.trim(),
        deviceId: auth.getDeviceId(),
        ...(verifying
          ? {
              verificationCode: code.value.trim(),
              expectedUserKey: auth.rememberedUserKey.value || undefined,
            }
          : {}),
      },
      { skipAuth: true, timeoutMs: 15_000 },
    )
    // Proxies may return HTML error pages. Do not surface WebKit's JSON parser error.
    const data = await res.json().catch(() => null)
    if (!res.ok)
      throw new Error(
        (typeof data?.message === 'string' && data.message) ||
          (res.status === 429
            ? 'Too many attempts. Please wait before trying again.'
            : res.status >= 500
              ? `The server is temporarily unavailable (${res.status}). Please try again shortly.`
              : verifying
                ? 'That code could not be verified. Check it or request a new one.'
                : 'Could not send a code. Please try again.'),
      )
    if (
      !data ||
      (verifying
        ? typeof data.accessToken !== 'string' ||
          !data.accessToken ||
          typeof data.refreshToken !== 'string' ||
          !data.refreshToken ||
          !data.account
        : data.ok !== true)
    ) {
      throw new Error('The server returned an unexpected response. Please try again shortly.')
    }
    if (verifying) {
      auth.acceptVerification(data)
      codeSent.value = false
      code.value = ''
    } else {
      codeSent.value = true
      info.value =
        'If this email belongs to an eligible account, a code has been sent. Check your inbox.'
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Unable to connect. Please try again.'
  } finally {
    busy.value = false
  }
}
function trapDialogFocus(event: KeyboardEvent) {
  if (event.key !== 'Tab') return
  const elements = dialogPanel.value?.querySelectorAll<HTMLElement>(
    'input:not([disabled]), button:not([disabled])',
  )
  if (!elements?.length) return
  const first = elements[0]
  const last = elements[elements.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}
function requestAnotherCode() {
  codeSent.value = false
  code.value = ''
  info.value = ''
}
function checkWhenVisible() {
  if (document.visibilityState === 'visible') void auth.checkSession()
}
function retryConnection() {
  void auth.checkSession(true)
}
onMounted(() => {
  void auth.checkSession(true)
  document.addEventListener('visibilitychange', checkWhenVisible)
  window.addEventListener('online', retryConnection)
})
onUnmounted(() => {
  document.removeEventListener('visibilitychange', checkWhenVisible)
  window.removeEventListener('online', retryConnection)
})
</script>
<template>
  <div
    v-if="
      hasLocalAccount && (sessionStatus === 'reconnect-required' || sessionStatus === 'signed-out')
    "
    role="status"
    class="flex flex-wrap items-center justify-between gap-2 border-b border-base-300 bg-base-200 px-4 py-2 text-sm"
  >
    <span
      >Your session ended. Reconnect to use server features. Your local timeline is still
      available.</span
    >
    <button class="btn btn-sm btn-primary" @click="reconnectOpen = true">Reconnect</button>
  </div>
  <div
    v-else-if="sessionStatus === 'unavailable'"
    role="status"
    class="flex flex-wrap items-center justify-between gap-2 border-b border-base-300 bg-base-200 px-4 py-2 text-sm"
  >
    <span
      >Unable to connect. Server changes may not be saved yet. Your local timeline is still
      available.</span
    >
    <button class="btn btn-sm" @click="retryConnection">Retry connection</button>
  </div>
  <div
    v-if="reconnectOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @keydown.esc="reconnectOpen = false"
    @keydown="trapDialogFocus"
  >
    <section
      ref="dialogPanel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconnect-title"
      class="w-full max-w-md rounded-xl bg-base-100 p-6 shadow-xl"
    >
      <h2 id="reconnect-title" class="text-xl font-semibold">Reconnect your account</h2>
      <p class="mt-2 text-sm">
        Verify your email to resume server access. Your timeline and current screen will stay here.
      </p>
      <form class="mt-4 flex flex-col gap-3" @submit.prevent="submit">
        <label class="flex flex-col gap-1"
          >Email
          <input
            v-model="email"
            autofocus
            type="email"
            autocomplete="email"
            required
            class="input w-full"
            :readonly="!!account?.email"
          />
        </label>
        <label v-if="codeSent" class="flex flex-col gap-1"
          >Sign-in code
          <input
            v-model="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]{6}"
            maxlength="6"
            required
            class="input w-full"
          />
        </label>
        <p v-if="info" role="status" class="text-sm">{{ info }}</p>
        <p v-if="error" role="alert" class="text-sm text-error">{{ error }}</p>
        <button class="btn btn-primary" :disabled="busy">
          {{ busy ? 'Please wait…' : codeSent ? 'Verify and reconnect' : 'Send me a sign-in code' }}
        </button>
        <button
          v-if="codeSent"
          type="button"
          class="btn btn-ghost"
          :disabled="busy"
          @click="requestAnotherCode"
        >
          Request another code
        </button>
        <button type="button" class="btn btn-ghost" :disabled="busy" @click="reconnectOpen = false">
          Continue locally
        </button>
      </form>
    </section>
  </div>
</template>
